import { useCallback, useEffect, useState } from 'react';

import { useRouter } from 'next/router';

import {
  sendOTP,
  verifyOTP
} from 'src/services/apiRequests/Login/LoginService';
import SimpleStore from 'src/store/SimpleStore';
import { colors } from 'src/styles/colors';
import { HTTP_HEADER_KEYS } from 'src/utils/constants/HttpMethodsConstants';
import { HamburgerMenuLinks } from 'src/utils/constants/RouterConstants';

import { useAppStore } from 'src/hooks/useAppStore';
import Button from '../buttons/Button';
import EnterOtp from '../loginPopup/enterOTP/EnterOtp';
import Modal from '../modal/Modal';
import SocialMediaLogin from '../SocialMediaLogin/SocialMediaLogin';
import {
  getParentalPopupDesc,
  getSocialLoginType
} from './parentalControlHelper';
import * as styles from './VerifyParentalOtpStyles';

interface verifyOTPResponse {
  code?: number;
  message?: string;
  access_token?: string;
  expires_in?: number;
  token_type?: string;
  refresh_token?: string;
}

interface VerifyParentalOtpProps {
  parentalFlag: boolean;
}
// removed loginTypeForOtp import from graphql types as it is auto generated
// loginTypeForOtp is not part of  graphql types
enum loginTypeForOtp {
  phoneNo = 'phoneno',
  email = 'email'
}

const VerifyParentalOtp: React.FC<VerifyParentalOtpProps> = ({
  parentalFlag
}) => {
  const [parentPopup, setParentPopup] = useState(true);
  const [inputType, setInputType] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [showOtpPopup, setShowOtpPopup] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string | undefined>();
  const router = useRouter();

  const { usersData, setParentalData, translations } = useAppStore();
  const ParentalControlStrings = translations?.common?.PARENTAL_CONTROL;

  useEffect(() => {
    updateInputTypeAndValue();
  }, []);

  useEffect(() => {
    setParentPopup(parentalFlag);
  }, [parentalFlag]);

  const updateInputTypeAndValue = () => {
    if (usersData?.mobile) {
      setInputType(loginTypeForOtp.phoneNo);
      setInputValue(usersData.mobile);
    } else {
      setInputType(loginTypeForOtp.email);
      setInputValue(usersData?.email as string);
    }
  };

  /**
   * handles the otp close button
   */
  const closeOtpPopup = useCallback(() => {
    setShowOtpPopup(false);
    setOtpErrorMessage(undefined);
    // setParentalFlag(false);
    setParentalData({ showParentalPinPageFlag: false });
  }, [setShowOtpPopup, setOtpErrorMessage, setParentalData]);

  /**
   * handles otp verification response
   * @param data response data from api
   */
  const handleOTPVerification = (data: verifyOTPResponse) => {
    if (data?.access_token) {
      setShowOtpPopup(false);
      SimpleStore.set(HTTP_HEADER_KEYS.AUTHORIZATION, data.access_token);
      router.push(HamburgerMenuLinks.PARENTAL_CONTROL);
    }
    if (data?.code === 2) {
      // code is invalid or expired
      setOtpErrorMessage(data.message);
    }
  };

  /**
   * Verifies the otp
   * @param otp OTP value
   * @param inputType email or phoneno
   * @param inputValue email id or phone number
   */
  const handleVerifyOtp = async (
    otp: string,
    inputType: string,
    inputValue: string
  ) => {
    try {
      const data = await verifyOTP(otp, inputType, inputValue);
      handleOTPVerification(data);
    } catch (error: any) {
      const errorObj = await error?.res.json();
      const errorMessage = errorObj?.message || ''; // passing all types error code messages not only errorObj?.code === 2
      setOtpErrorMessage(errorMessage);
    }
  };

  /**
   * Generate the OTP
   * @param inputType email or phone
   * @param inputValue emai id or phone number
   */
  const generateOTP = async (inputType: string, inputValue: string) => {
    await sendOTP(inputType, inputValue);
  };

  /**
   * handles the click of edit email or number
   */
  const handleEditInput = () => {
    setShowOtpPopup(false);
  };

  /**
   * handles close parent popup
   */
  const closeParentalPoupup = () => {
    setParentPopup(false);
    setParentalData({ showParentalPinPageFlag: false });
  };

  /**
   * handles close parent popup
   */
  const handleParentPopupBtn = () => {
    setParentPopup(false);
    setShowOtpPopup(true);
    generateOTP(inputType, inputValue);
  };

  const onClose = () => {
    setParentalData({ showParentalPinPageFlag: false });
  };

  return (
    <>
      <Modal
        show={parentPopup}
        onClose={closeParentalPoupup}
        width="500px"
        height="500px"
        background={colors.primaryBgColor}
        // isLoading={loadingSpinner}
        renderCloseIcon={true}
        closeIconColor={colors.white}
        padding="30px">
        <styles.parentalPopupWrap socialLoginType={getSocialLoginType()}>
          <h2 css={styles.title}>
            {ParentalControlStrings.WebParental_Msg_VerifyAccount_Text}
          </h2>
          <p css={styles.descripton}>
            {getParentalPopupDesc(inputType, ParentalControlStrings)}
          </p>
          {getSocialLoginType() ? (
            <SocialMediaLogin
              onClose={onClose}
              className="parentalSocialLoginWrap"
            />
          ) : (
            <Button
              callback={handleParentPopupBtn}
              customStyles={styles.btn}
              id="parental-pin-send-otp-btn">
              {ParentalControlStrings.CTA_Send_OTP}
            </Button>
          )}
        </styles.parentalPopupWrap>
      </Modal>
      <Modal
        show={showOtpPopup}
        width="500px"
        height="500px"
        background={colors.white}
        onClose={closeOtpPopup}
        renderCloseIcon={true}
        closeIconColor={colors.black}
        padding="30px">
        <EnterOtp
          inputType={inputType}
          inputValue={inputValue}
          show={showOtpPopup}
          hideEditOption={true}
          onClose={closeOtpPopup}
          otpErrorMessage={otpErrorMessage}
          setOtpErrorMessage={setOtpErrorMessage}
          handleVerifyOtp={handleVerifyOtp}
          resendOTP={generateOTP}
          handleEditInput={handleEditInput}
        />
      </Modal>
    </>
  );
};

export default VerifyParentalOtp;
